<template>
  <ChannelChatSkeleton v-if="loading" v-bind="$attrs" />

  <div
    v-else-if="roomId === null"
    class="flex flex-col w-full gap-2.5 max-depx-2"
  >
    <div
      class="flex items-center justify-center w-full pt-4 max-large-desktop:hidden"
    >
      <ChatOnlineUsers />
    </div>
    <ul v-for="room in filteredChatRooms" :key="room.id">
      <ChannelChatRoomButton :chat-room="room" @click="selectRoom" />
    </ul>
  </div>

  <ChatRoomProvider v-else :chat-room-id="roomId">
    <ChannelChatRoom :chat-room-id="roomId" @close="onCloseChannelChatRoom" />
  </ChatRoomProvider>
</template>

<script lang="ts" setup>
import { useChatStore } from "@/stores/chat/chat.store";
import { computed, ref, watch } from "vue";
import ChannelChatRoom from "./channel-chat-room.vue";
import ChatRoomProvider from "./chat-room-provider.vue";
import ChannelChatRoomButton from "./channel-chat-room-button.vue";
import ChatOnlineUsers from "../chat-online-users.vue";
import { useChannelStore } from "@/stores/channel.store";
import { useActiveEnumValue } from "@/composables/use-active-enum-value";
import ChannelChatSkeleton from "@/components/ui/skeleton/channel-chat-skeleton.vue";

const channelStore = useChannelStore();

const selectedCommunity = computed(() => {
  return channelStore.channelCommunities.find(
    (community) => community.id === chatStore.selectedCommunity,
  );
});

enum ChatRoomState {
  DEFAULT = "DEFAULT",
  NONE = "NONE",
  MANUAL = "MANUAL",
}
const chatRoomState = ref<
  | {
      state: ChatRoomState.DEFAULT;
    }
  | {
      state: ChatRoomState.NONE;
    }
  | {
      state: ChatRoomState.MANUAL;
      selectedRoomId: string;
    }
>({
  state: ChatRoomState.DEFAULT,
});

const chatStore = useChatStore();

const loading = computed(() => chatStore.fetching || !channelStore.channel);

enum ChatRoomType {
  COMMUNITY = "COMMUNITY",
  CHANNEL = "CHANNEL",
}

const channelFilter = useActiveEnumValue(
  [ChatRoomType.CHANNEL, ChatRoomType.COMMUNITY],
  ChatRoomType.COMMUNITY,
);

watch(
  selectedCommunity,
  (value) => {
    channelFilter.value = value ? ChatRoomType.COMMUNITY : ChatRoomType.CHANNEL;
    chatRoomState.value = {
      state: ChatRoomState.DEFAULT,
    };
  },
  {
    immediate: true,
  },
);

const filteredChatRooms = computed(() => {
  const list = chatStore.chatRooms;
  if (!list) return [];
  if (chatStore.isAllChannelRoomsVisible)
    return [...chatStore.chatRooms.values()];

  const result = [];
  for (const [, chatRoom] of list) {
    if (
      channelFilter.community &&
      chatRoom.communityId !== selectedCommunity.value?.id
    ) {
      continue;
    }

    if (channelFilter.channel && chatRoom.communityId !== null) {
      continue;
    }

    result.push(chatRoom);
  }

  return result;
});

const defaultRoomId = computed(() => {
  return filteredChatRooms.value.find((room) => room.isDefault)?.id || null;
});

const roomId = computed((): string | null => {
  switch (chatRoomState.value.state) {
    case ChatRoomState.DEFAULT:
      return defaultRoomId.value;
    case ChatRoomState.MANUAL:
      return chatRoomState.value.selectedRoomId;
    case ChatRoomState.NONE:
    default:
      return null;
  }
});

function selectRoom(roomId: string) {
  chatRoomState.value = {
    state: ChatRoomState.MANUAL,
    selectedRoomId: roomId,
  };
}

function onCloseChannelChatRoom() {
  chatRoomState.value = {
    state: ChatRoomState.NONE,
  };
}
</script>
